import React, { useEffect, useState } from "react";
import { HttpStatusCode } from "axios";
import clsx from "clsx";
import { useNavigate, useParams } from "react-router-dom";

import { useUserContext } from "../../../contexts/userContext";
import {
  getSpaceReview,
  getSpaceReviewSummary,
  postReview,
  ReviewItem,
  SpaceReviewSummaryResponse,
  updateReview,
} from "../../../services/spaceServices";

import useSimplePopup from "../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import CommonButton from "../../../components/Button";
import StickyHeader from "../../../components/Layout/StickyHeader";
import StarScore from "../../../components/Talk/StarScore";
import CommonPopup from "../../../components/Popup";
import CommonTextArea from "../../../components/TextArea";
import TalkEachReview from "../../../components/Talk/EachReview";

type Props = {};

export default function ReviewStore(props: Props) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [reviewList, setReviewList] = useState<ReviewItem[]>([]);
  const [popup, setPopup] = useState<string>("");
  const [reviewScore, setReviewScore] = useState<number>(0);
  const [reviewContent, setReviewContent] = useState<string>("");
  const [reviewSummary, setReviewSummary] =
    useState<SpaceReviewSummaryResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [selectedReview, setSelectedReview] = useState<ReviewItem | null>(null);
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [user] = useUserContext();

  const getScoreText = (score: number) => {
    if (score === 0) {
      return "별점을 선택해 주세요";
    } else if (score >= 4.5) {
      return "최고예요";
    } else if (score >= 3.5) {
      return "좋아요";
    } else if (score >= 2.5) {
      return "보통이에요";
    } else if (score >= 1.5) {
      return "그저그래요";
    } else {
      return "별로에요";
    }
  };

  const loadSpaceReviewSummary = async () => {
    if (!id) return;

    try {
      const res = await getSpaceReviewSummary({ spaceId: id });
      if (res.status === HttpStatusCode.Ok) {
        await setReviewSummary(res.data);
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("리뷰 요약 정보를 가져오는데 실패했습니다.");
      }
    }
  };

  const loadSpaceReview = async () => {
    if (!id) return;

    try {
      const res = await getSpaceReview({ spaceId: id });
      if (res.status === HttpStatusCode.Ok) {
        await setReviewList(res.data.items);
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("리뷰 정보를 가져오는데 실패했습니다.");
      }
    }
  };

  const loadReviewData = () => {
    setIsLoading(true);
    Promise.allSettled([loadSpaceReview(), loadSpaceReviewSummary()]).finally(
      () => {
        setIsLoading(false);
      }
    );
  };

  const validateWrite = (error?: boolean) => {
    if (reviewScore === 0) {
      if (error) open("별점을 선택해주세요");
      return false;
    }

    return true;
  };

  const validateModify = (error?: boolean) => {
    if (!selectedReview) return false;

    if (selectedReview?.rating === 0) {
      open("별점을 선택해주세요");
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (user.isLoading) return;
    loadReviewData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isLoading]);

  return (
    <div className="pb-24">
      {loadingComponent}
      <div className="bg-brand-gray-140 px-5 text-white">
        <StickyHeader
          title={`${reviewSummary?.spaceName ?? ""} 리뷰`}
          onBackClick={() => navigate("/talk")}
          className="!bg-brand-gray-140"
          backIcon={<StoreBackIcon />}
        />
        <div className="flex flex-col items-center pb-10">
          <div className=" text-[42px] font-semibold">
            {reviewSummary?.rating ?? 0}
          </div>
          <div className="flex gap-2">
            <StarScore score={reviewSummary?.rating ?? 0} size="M" />
            <div>{reviewSummary?.countReview ?? 0}건</div>
          </div>
        </div>
      </div>
      <div className="p-5">
        {/* JIRA-291 리포팅에서 중복 리뷰 허용한다고 전달받음(홍다미님) */}
        {/* {reviewSummary?.isReviewedStore !== true && ( */}
        <CommonButton
          variant="tertiary"
          className="w-full text-sm py-[11px] mb-5"
          onClick={() => setPopup("writeReview")}
        >
          리뷰 작성하기
        </CommonButton>
        {/* )} */}
      </div>
      <div className="px-5 flex flex-col gap-[15px]">
        {reviewList?.map((review) => (
          <TalkEachReview
            key={review.lifeSpaceReviewId}
            review={review}
            spaceId={id as string}
            onLoad={loadReviewData}
            onPopup={(text: string, item: any) => {
              setSelectedReview(item);
              setPopup(text);
            }}
          />
        ))}
      </div>

      <CommonPopup
        isOpen={popup === "writeReview"}
        title="리뷰 작성"
        padding={0}
        onClose={() => {
          setReviewContent("");
          setReviewScore(0);
          setIsLoading(false);
          setPopup("");
        }}
      >
        <div>
          <div className=" py-6 text-center text-sm text-brand-neutral-90 bg-[#fafafa]">
            맛있게 드셨나요? 후기를 남겨주세요
          </div>
          <div className="pb-5 px-5 flex flex-col gap-2.5">
            <div className="flex flex-col items-center py-5">
              <StarScore
                size="L"
                score={reviewScore}
                onClick={(score: number) => setReviewScore(score)}
              />
              <div
                className={clsx([
                  "text-base",
                  {
                    "text-brand-neutral-40": reviewScore === 0,
                  },
                ])}
              >
                {getScoreText(reviewScore)}
              </div>
            </div>
            <CommonTextArea
              placeholder="이용 후기를 남겨주세요"
              value={reviewContent}
              onChange={(e) => setReviewContent(e.target.value.slice(0, 500))}
            />
          </div>
          <CommonButton
            className="w-full rounded-none h-bottom-button"
            validation={!validateWrite()}
            onClick={async () => {
              if (!validateWrite(true)) return;

              setIsLoading(true);
              try {
                await postReview(id ?? "", {
                  content: reviewContent,
                  rating: reviewScore,
                });
              } catch (e: any) {
                open("만족도 평가 저장에 실패했습니다.");
              } finally {
                await loadReviewData();
                setPopup("");
                setReviewContent("");
                setReviewScore(0);
                setIsLoading(false);
              }
            }}
          >
            작성 완료
          </CommonButton>
        </div>
      </CommonPopup>

      <CommonPopup
        isOpen={popup === "modifyReview"}
        title="리뷰 수정"
        padding={0}
        onClose={() => setPopup("")}
        unUseHorizonRule={false}
      >
        <div>
          <div className=" py-6 text-center text-sm text-brand-neutral-90 bg-[#fafafa]">
            맛있게 드셨나요? 후기를 남겨주세요
          </div>
          <div className="pb-5 px-5 flex flex-col gap-2.5">
            <div className="flex flex-col items-center py-5">
              <StarScore
                size="L"
                score={selectedReview?.rating ?? 0}
                onClick={(score: number) =>
                  setSelectedReview(
                    (prev) => ({ ...prev, rating: score } as ReviewItem)
                  )
                }
              />
              <div
                className={clsx([
                  "text-base",
                  {
                    "text-brand-neutral-40":
                      (selectedReview?.rating ?? 0) === 0,
                  },
                ])}
              >
                {getScoreText(selectedReview?.rating ?? 0)}
              </div>
            </div>
            <CommonTextArea
              placeholder="이용 후기를 남겨주세요"
              value={selectedReview?.content ?? ""}
              onChange={(e) =>
                setSelectedReview(
                  (prev) =>
                    ({
                      ...prev,
                      content: e.target.value.slice(0, 500),
                    } as ReviewItem)
                )
              }
            />
            <div className="text-xs text-brand-neutral-40">
              <span className="text-brand-secondary-error">*</span> 리뷰 수정 시
              답변이 초기화 됩니다.
            </div>
          </div>
          <CommonButton
            className="w-full rounded-none h-bottom-button"
            validation={!validateModify()}
            onClick={async () => {
              if (!validateModify(true)) return;

              setIsLoading(true);
              try {
                if (selectedReview?.lifeSpaceReviewId) {
                  await updateReview(selectedReview?.lifeSpaceReviewId, {
                    content: selectedReview.content,
                    rating: selectedReview.rating ?? 0,
                  });
                }
                await loadReviewData();
                setPopup("");
              } catch (e: any) {
                if (e?.response?.data?.error) {
                  open(e.response.data.error?.replace(". ", ".\n"));
                } else {
                  open("리뷰 수정에 실패했습니다.");
                }
              } finally {
                setIsLoading(false);
              }
            }}
          >
            수정 완료
          </CommonButton>
        </div>
      </CommonPopup>

      {simplePopupComponent}
    </div>
  );
}

export const StoreBackIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.9998 12H6.29871"
      stroke="white"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M12 19L5 12L12 5"
      stroke="white"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);
